import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import { useCurrency, useI18n } from '~Hooks';
import type { LocalItem, TranslatedString } from '~Types';
import type { Choices } from '..';
import { checkOutOfStock } from '~Scenes/SelfServiceKiosks/utils/inventory';
import { useInventoryUniverse } from '~Components/InventoryUniverse';
import Icon from '~ComponentsImproved/Icon';
import PlusIcon from '~Scenes/SelfServiceKiosks/Assets/Icons/plus.svg';
import MinusIcon from '~Scenes/SelfServiceKiosks/Assets/Icons/minus.svg';
import { Box } from '~Styles/box';

const imageSize = {
  width: 144,
  height: 96,
};

const move = keyframes`
  50% {
    transform: rotate(-45deg) translate(-200%, 0) rotate(45deg);
  }
  75% {
    transform: translate(-200%, 100vh);
    opacity: 0;
  }
  100% {
    transform: translate(-200%, 100vh);
    opacity: 0;
  }
`;

const Wrapper = styled.div<{
  selected: boolean;
  pressed: boolean;
  isUnavailable: boolean;
}>`
  display: flex;
  height: 105px;
  align-content: center;
  flex-direction: row;
  justify-content: flex-start;
  border-bottom: 1px solid rgb(224, 224, 227);
  position: relative;
  opacity: ${({ pressed }) => (pressed ? 0.5 : 1)};
  color: ${({ selected, isUnavailable }) => {
    if (isUnavailable) {
      return '#757578';
    } else if (selected) {
      return 'rgb(32, 153, 70)';
    } else {
      return 'rgb(33, 33, 36)';
    }
  }};
  transition: 0.35s opacity, color 0.3s;
`;

const Title = styled.strong`
  font-family: Eina01-SemiBold;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 32px;
`;

const Price = styled.span`
  font-family: Eina01-Regular;
  font-size: 24px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 32px;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
`;

const Add = styled.span<{
  selected: boolean;
  isUnavailable: boolean;
  isMultiSelect?: boolean;
}>`
  align-self: center;
  color: ${({ selected, isUnavailable, isMultiSelect }) => {
    if (isUnavailable) {
      return '#bdbdc0';
    } else if (selected) {
      return isMultiSelect ? 'rgb(33, 33, 36)' : 'rgb(32, 153, 70)';
    } else {
      return 'rgb(117, 117, 120)';
    }
  }};
  font-family: ${({ selected }) =>
    selected ? 'Eina01-SemiBold' : 'Eina01-Regular'};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 24px;
  margin-right: ${({ isMultiSelect }) => (isMultiSelect ? 'none' : '32px')};
`;

const ImageWrapper = styled.div<{ url: string; isUnavailable?: boolean }>`
  width: ${imageSize.width}px;
  height: ${imageSize.height}px;
  align-self: center;
  background: url('${({ url }) => url}') center center no-repeat;
  background-size: cover;
  opacity: ${({ isUnavailable }) => (isUnavailable ? 0.6 : 1)};
`;

const Image = styled(ImageWrapper)<{ touched: boolean }>`
  animation: ${({ touched }) => (touched ? move : '')} 0.5s ease-in;
`;

const RoundButton = styled.div<{
  selected: boolean;
  isUnavailable: boolean;
  color?: string;
}>`
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: ${({ selected, isUnavailable }) => {
    if (isUnavailable) {
      return '1px solid #bdbdc0';
    } else if (selected) {
      return '1px solid rgb(33, 33, 36)';
    } else {
      return '1px solid rgb(117, 117, 120)';
    }
  }};
`;

interface Props {
  price: number;
  titleLang: TranslatedString;
  image: {
    file: {
      url: string;
    };
  };
  onAddUpsellItem: () => void;
  onRemoveUpsellItem: (id: string) => void;
  choices: Choices;
  id: string;
  cartItems: LocalItem[];
  enableMultiSelect: boolean;
}

const Item = ({
  id,
  price,
  titleLang,
  image,
  onAddUpsellItem,
  onRemoveUpsellItem,
  choices,
  cartItems,
  enableMultiSelect,
}: Props) => {
  const { i18n } = useI18n();
  const inventory = useInventoryUniverse();
  const currency = useCurrency();
  const isOutOfStock = checkOutOfStock({
    id,
    inventoryItems: inventory.items,
    disregardCount: false,
    cartItems,
  });

  const [pressed, setPressed] = useState<boolean>(false);
  const [touched, setTouched] = useState<boolean>(false);

  const url = image?.file?.url || '';
  const choice = choices[id];
  const selected = Boolean(choice);

  useEffect(() => {
    if (!selected && touched) {
      setTouched(false);
    }
  }, [selected, touched]);

  const handleClick = () => {
    if (isOutOfStock) {
      return;
    }

    if (enableMultiSelect) {
      /* This makes sure that every item image will fly into the Basket when they get added */
      setTouched(false);
      setTimeout(() => setTouched(true), 0);
    } else {
      setTouched(true);
    }

    onAddUpsellItem();
  };

  const getIconColor = () => {
    if (isOutOfStock) {
      return '#bdbdc0';
    } else if (selected) {
      return 'rgb(33, 33, 36)';
    } else {
      return 'rgb(117, 117 ,120)';
    }
  };

  return (
    <Wrapper
      /* The following 3 is undefined if multiselect because we use -/+ buttons to add/remove upsells in that case instead */
      onClick={enableMultiSelect ? undefined : handleClick}
      onTouchStart={enableMultiSelect ? undefined : () => setPressed(true)}
      onTouchEnd={enableMultiSelect ? undefined : () => setPressed(false)}
      isUnavailable={isOutOfStock}
      selected={selected}
      pressed={pressed}
    >
      <Col>
        <Title>{i18n(titleLang)}</Title>
        <Price>{currency.format(price)}</Price>
      </Col>

      {enableMultiSelect ? (
        <Box display="flex" alignItems="center" gap="small" marginRight="large">
          <RoundButton
            selected={selected}
            isUnavailable={isOutOfStock}
            onClick={() => selected && onRemoveUpsellItem(choice.id)}
          >
            <Icon src={MinusIcon} color={getIconColor()} />
          </RoundButton>

          <Add selected={selected} isUnavailable={isOutOfStock} isMultiSelect>
            {choice?.count || 0}
          </Add>

          <RoundButton
            selected={selected}
            isUnavailable={isOutOfStock}
            onClick={handleClick}
          >
            <Icon src={PlusIcon} color={getIconColor()} />
          </RoundButton>
        </Box>
      ) : (
        <Add selected={selected} isUnavailable={isOutOfStock}>
          {selected ? i18n('Express.Cart.Chosen') : i18n('Express.Cart.Choose')}
        </Add>
      )}

      <ImageWrapper
        url={`${url}/${imageSize.width * 2}x${imageSize.height * 2}`}
        isUnavailable={isOutOfStock}
      >
        <Image
          url={`${url}/${imageSize.width * 2}x${imageSize.height * 2}`}
          touched={touched}
        />
      </ImageWrapper>
    </Wrapper>
  );
};

export default Item;